/**
 * Queue list loading UI — matches
 * `docs/Central Product Master (2)/Verification Queue.dc.html` listLoading block:
 * header strip (spin + uppercase label) + 7 shimmer rows.
 */

const ROW_WIDTHS = [
    { title: 78, sub: 46, mid: 70 },
    { title: 62, sub: 58, mid: 55 },
    { title: 88, sub: 38, mid: 64 },
    { title: 70, sub: 52, mid: 48 },
    { title: 55, sub: 44, mid: 76 },
    { title: 83, sub: 60, mid: 58 },
    { title: 66, sub: 41, mid: 67 },
] as const;

interface CpmSkeletonRowProps {
    /** Kept for call-site compatibility; design uses one fixed skeleton grid. */
    gridTemplateColumns?: string;
    cellCount?: number;
    className?: string;
    /** Varied shimmer widths for this row index (0–6). */
    index?: number;
}

/** Single structured shimmer row (checkbox + two stacked cols + chip + wait + CTA). */
export default function CpmSkeletonRow({ className = '', index = 0 }: CpmSkeletonRowProps) {
    const widths = ROW_WIDTHS[index % ROW_WIDTHS.length];

    return (
        <div
            className={[
                'grid items-center border-b border-[var(--cpm-border-default)]',
                className,
            ].join(' ')}
            style={{
                gridTemplateColumns: '38px minmax(0,1.4fr) minmax(0,1.1fr) 150px 100px 170px',
                gap: 14,
                padding: '16px 18px',
            }}
            aria-hidden
        >
            <span className="cpm-sk" style={{ width: 18, height: 18, borderRadius: 5 }} />
            <div className="flex flex-col min-w-0" style={{ gap: 8 }}>
                <span className="cpm-sk" style={{ height: 13, width: `${widths.title}%` }} />
                <span className="cpm-sk" style={{ height: 10, width: `${widths.sub}%` }} />
            </div>
            <div className="flex flex-col min-w-0" style={{ gap: 8 }}>
                <span className="cpm-sk" style={{ height: 12, width: `${widths.mid}%` }} />
                <span className="cpm-sk" style={{ height: 10, width: '44%' }} />
            </div>
            <span className="cpm-sk" style={{ height: 18, width: 80, borderRadius: 999 }} />
            <span className="cpm-sk" style={{ height: 12, width: 56 }} />
            <span
                className="cpm-sk justify-self-end"
                style={{ height: 30, width: 120, borderRadius: 8 }}
            />
        </div>
    );
}

interface CpmQueueListLoadingProps {
    label?: string;
    rowCount?: number;
    className?: string;
}

/** Full list-loading chrome: spin header + N skeleton rows. */
export function CpmQueueListLoading({
    label = 'Loading the queue',
    rowCount = 7,
    className = '',
}: CpmQueueListLoadingProps) {
    return (
        <div className={className} aria-busy="true" aria-label={label}>
            <div
                className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                style={{ gap: 10, padding: '12px 18px' }}
            >
                <span className="cpm-spin" />
                <span
                    className="uppercase text-[var(--cpm-text-tertiary)]"
                    style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    {label}
                </span>
            </div>
            {Array.from({ length: rowCount }, (_, i) => (
                <CpmSkeletonRow key={i} index={i} />
            ))}
        </div>
    );
}

const VOLUME_ROW_WIDTHS = [78, 54, 66, 48, 72, 60, 80, 52, 68, 58, 74, 62] as const;

interface CpmVolumeTableSkeletonProps {
    label?: string;
    rowCount?: number;
    className?: string;
    /** When true, also render the header-card shimmer above the table. */
    withHeaderCard?: boolean;
}

/** Volume Settings boot skeleton — header card + 12-row table. */
export function CpmVolumeTableSkeleton({
    label = 'Loading volume settings',
    rowCount = 12,
    className = '',
    withHeaderCard = true,
}: CpmVolumeTableSkeletonProps) {
    return (
        <div className={['flex flex-col', className].join(' ')} style={{ gap: 22 }} aria-busy="true">
            {withHeaderCard ? (
                <div
                    className="flex items-start rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)]"
                    style={{
                        gap: 18,
                        padding: 20,
                        boxShadow:
                            '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                    }}
                    aria-hidden
                >
                    <span className="cpm-sk shrink-0" style={{ width: 38, height: 38, borderRadius: 10 }} />
                    <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 9 }}>
                        <span className="cpm-sk" style={{ height: 20, width: 180 }} />
                        <span className="cpm-sk" style={{ height: 13, width: 320, maxWidth: '100%' }} />
                        <span className="cpm-sk" style={{ height: 12, width: 240, maxWidth: '100%' }} />
                    </div>
                    <span className="cpm-sk shrink-0" style={{ width: 132, height: 40, borderRadius: 10 }} />
                </div>
            ) : null}

            <div
                className="rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)] overflow-hidden"
                style={{
                    boxShadow:
                        '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                }}
                aria-label={label}
            >
                <div
                    className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                    style={{ gap: 10, padding: '12px 18px' }}
                >
                    <span className="cpm-spin" />
                    <span
                        className="uppercase text-[var(--cpm-text-tertiary)]"
                        style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                    >
                        {label}
                    </span>
                </div>
                {Array.from({ length: rowCount }, (_, i) => {
                    const w = VOLUME_ROW_WIDTHS[i % VOLUME_ROW_WIDTHS.length];
                    return (
                        <div
                            key={i}
                            className="grid items-center border-b border-[var(--cpm-border-default)]"
                            style={{
                                gridTemplateColumns: '88px 60px 108px minmax(0,1fr) minmax(240px,auto)',
                                gap: 14,
                                padding: '14px 18px',
                            }}
                            aria-hidden
                        >
                            <span
                                className="cpm-sk justify-self-end"
                                style={{ height: 15, width: 52 }}
                            />
                            <span className="cpm-sk" style={{ height: 12, width: 30 }} />
                            <span
                                className="cpm-sk"
                                style={{ height: 22, width: 82, borderRadius: 999 }}
                            />
                            <span className="cpm-sk" style={{ height: 12, width: `${w}%` }} />
                            <span
                                className="cpm-sk justify-self-end"
                                style={{ height: 32, width: 190, borderRadius: 9 }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

interface CpmMasterTableSkeletonProps {
    label?: string;
    rowCount?: number;
    className?: string;
}

/**
 * Master Editor table loading chrome — matches design grid
 * `38px minmax(0,1.5fr) minmax(0,1fr) 150px 118px 128px`, 8 shimmer rows with
 * varied title/sub/mid widths (design `skeletonRows` generator).
 */
export function CpmMasterTableSkeleton({
    label = 'Loading records',
    rowCount = 8,
    className = '',
}: CpmMasterTableSkeletonProps) {
    return (
        <div className={className} aria-busy="true" aria-label={label}>
            <div
                className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                style={{ gap: 10, padding: '12px 18px' }}
            >
                <span className="cpm-spin" />
                <span
                    className="uppercase text-[var(--cpm-text-tertiary)]"
                    style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    {label}
                </span>
            </div>
            {Array.from({ length: rowCount }, (_, i) => {
                const titleW = 58 + ((i * 7) % 34);
                const subW = 30 + ((i * 11) % 26);
                const midW = 44 + ((i * 13) % 38);
                return (
                    <div
                        key={i}
                        className="grid items-center border-b border-[var(--cpm-border-default)]"
                        style={{
                            gridTemplateColumns: '38px minmax(0,1.5fr) minmax(0,1fr) 150px 118px 128px',
                            gap: 14,
                            padding: '15px 18px',
                        }}
                        aria-hidden
                    >
                        <span className="cpm-sk" style={{ width: 18, height: 18, borderRadius: 5 }} />
                        <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                            <span className="cpm-sk" style={{ height: 13, width: `${titleW}%` }} />
                            <span className="cpm-sk" style={{ height: 10, width: `${subW}%` }} />
                        </div>
                        <span className="cpm-sk" style={{ height: 12, width: `${midW}%` }} />
                        <span className="cpm-sk" style={{ height: 12, width: '66%' }} />
                        <span
                            className="cpm-sk"
                            style={{ height: 20, width: 78, borderRadius: 999 }}
                        />
                        <span
                            className="cpm-sk justify-self-end"
                            style={{ height: 26, width: 84, borderRadius: 999 }}
                        />
                    </div>
                );
            })}
        </div>
    );
}

interface CpmTableSkeletonProps {
    label?: string;
    rowCount?: number;
    className?: string;
    /** Queue-style rows (default), Volume Settings rows, or Master Editor rows. */
    variant?: 'queue' | 'volume' | 'master';
    withHeaderCard?: boolean;
}

/** Shared table loading chrome — queue, volume, or master variants. */
export function CpmTableSkeleton({
    label,
    rowCount,
    className = '',
    variant = 'queue',
    withHeaderCard = false,
}: CpmTableSkeletonProps) {
    if (variant === 'volume') {
        return (
            <CpmVolumeTableSkeleton
                label={label ?? 'Loading volume settings'}
                rowCount={rowCount ?? 12}
                className={className}
                withHeaderCard={withHeaderCard}
            />
        );
    }
    if (variant === 'master') {
        return (
            <CpmMasterTableSkeleton
                label={label ?? 'Loading records'}
                rowCount={rowCount ?? 8}
                className={className}
            />
        );
    }
    return (
        <CpmQueueListLoading
            label={label ?? 'Loading the queue'}
            rowCount={rowCount ?? 7}
            className={className}
        />
    );
}
